import { MapPinOff, ShieldAlert } from 'lucide-react';
import type { RefObject } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import type { ZonesForMapState } from '../hooks/use-zones-for-map';

interface ZonesMapAreaProps {
  tileKey: string | null | undefined;
  containerRef: RefObject<HTMLDivElement | null>;
  state: ZonesForMapState;
}

/** A zones map's area, in the map view or the table's zone sheet. The map's container is rendered as soon as the key is known, so the map starts loading tiles while the zones are still on their way. */
export function ZonesMapArea({ tileKey, containerRef, state }: ZonesMapAreaProps) {
  if (tileKey === undefined) return <MapSkeleton />;
  if (tileKey === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-line bg-page px-4 text-center">
        <MapPinOff className="size-5 text-muted" />
        <p className="max-w-sm text-sm text-muted">
          Map key not configured — this deployment hasn't set a MapTiler key. Zones can still be listed in the table and saved without a shape.
        </p>
      </div>
    );
  }
  return (
    <>
      <div ref={containerRef} className="h-full w-full rounded-lg border border-line" />
      {state.status === 'loading' && (
        <div className="absolute inset-0">
          <MapSkeleton />
        </div>
      )}
      {state.status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-surface/90">
          <EmptyState
            icon={ShieldAlert}
            title="Couldn't load the zones"
            description="Something went wrong loading the zones for the map. Try reloading the page."
          />
        </div>
      )}
    </>
  );
}

function MapSkeleton() {
  return (
    <SkeletonRegion label="Loading zone map" className="h-full">
      <Skeleton className="h-full rounded-lg" />
    </SkeletonRegion>
  );
}
